package dev.lingvo.shared.screenshots.frames

import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.geom.apply
import dev.lingvo.shared.screenshots.geom.scale
import dev.lingvo.shared.screenshots.geom.times
import dev.lingvo.shared.screenshots.geom.translate
import dev.lingvo.shared.screenshots.render.Affine
import dev.lingvo.shared.screenshots.render.Path
import dev.lingvo.shared.screenshots.render.PathBuilder
import dev.lingvo.shared.screenshots.render.PathOp
import dev.lingvo.shared.screenshots.render.Paths
import kotlin.math.min

/** Fits the complete hardware footprint; screen proportions are independent of the available destination. */
fun FrameSpec.layout(box: Rect, screenAspect: Double, screenRadius: Double? = null): FrameLayout {
    require(listOf(box.x, box.y, box.w, box.h, screenAspect).all { it.isFinite() } && box.w > 0 && box.h > 0 && screenAspect > 0) {
        "Frame layout requires finite positive dimensions"
    }
    require(screenRadius == null || screenRadius.isFinite() && screenRadius >= 0) { "Invalid screen radius" }
    val landscape = screenAspect < 1 && kind !in listOf(FrameKind.LAPTOP, FrameKind.BROWSER, FrameKind.TV, FrameKind.NONE)
    val aspect = if (landscape) 1 / screenAspect else screenAspect
    val screen = Rect(bezel.left, bezel.top, 1.0, aspect)
    val lid = Rect(0.0, 0.0, 1 + bezel.left + bezel.right, aspect + bezel.top + bezel.bottom)
    val radius = if (kind == FrameKind.NONE) screenRadius ?: this.screenRadius else this.screenRadius
    val outerRadius = when (kind) {
        FrameKind.LAPTOP -> .018
        FrameKind.WATCH_ROUND -> lid.w / 2
        else -> radius + min(bezel.left, bezel.top)
    }
    val structures = structure(lid)
    val outer = when (kind) {
        FrameKind.LAPTOP -> if (Extra.LAPTOP_BASE in extras) Rect(-.06 * lid.w, 0.0, lid.w * 1.12, lid.h + .030) else lid
        FrameKind.TV -> if (Extra.TV_STAND in extras) Rect(0.0, 0.0, lid.w, lid.h + .042) else lid
        else -> lid
    }
    val buttonRects = buttons.map { button -> buttonRect(lid, button) }
    val bands = if (Extra.BAND_STUBS in extras) bandRects(lid) else emptyList()
    val footprint = union(listOf(outer) + buttonRects + bands)
    val orientation = if (landscape) Affine(0.0, -1.0, 1.0, 0.0, 0.0, lid.w) else Affine.IDENTITY
    val oriented = footprint.mapped(orientation)
    val factor = min(box.w / oriented.w, box.h / oriented.h)
    val fit = translate(box.x + (box.w - oriented.w * factor) / 2 - oriented.x * factor,
        box.y + (box.h - oriented.h * factor) / 2 - oriented.y * factor) * scale(factor, factor) * orientation
    val body = if (kind == FrameKind.WATCH_ROUND) Paths.ellipse(lid) else roundedRect(lid, outerRadius, smoothing)
    val mask = when (kind) {
        FrameKind.WATCH_ROUND -> Paths.ellipse(screen)
        FrameKind.BROWSER -> browserMask(screen, radius)
        else -> roundedRect(screen, radius, smoothing)
    }
    return FrameLayout(
        outer.mapped(fit), outerRadius * factor, screen.mapped(fit), radius * factor,
        Path(body.ops + structures.flatMap { it.ops }).mapped(fit), mask.mapped(fit), cutout?.path(screen)?.mapped(fit),
        buttons.zip(buttonRects).map { (b, r) -> roundedRect(r, b.radius ?: min(r.w, r.h) / 2, 0.0).mapped(fit) },
        decorations(screen, lid).map { it.mapped(fit) },
    )
}

private fun FrameSpec.structure(lid: Rect): List<Path> = when (kind) {
    FrameKind.LAPTOP -> if (Extra.LAPTOP_BASE !in extras) emptyList() else listOf(PathBuilder().moveTo(0.0, lid.bottom).lineTo(lid.right, lid.bottom)
        .lineTo(lid.right + .06 * lid.w, lid.bottom + .030).lineTo(-.06 * lid.w, lid.bottom + .030).close().build())
    FrameKind.TV -> if (Extra.TV_STAND !in extras) emptyList() else listOf(Paths.rect(Rect((lid.w - .08) / 2, lid.bottom, .08, .030)),
        roundedRect(Rect((lid.w - .32) / 2, lid.bottom + .030, .32, .012), .006, 0.0))
    FrameKind.WATCH_RECT -> if (Extra.BAND_STUBS in extras) bandRects(lid).map { roundedRect(it, .04, 0.0) } else emptyList()
    else -> emptyList()
}

private fun bandRects(lid: Rect): List<Rect> {
    val width = .78 * lid.w
    return listOf(Rect((lid.w - width) / 2, -.18, width, .18), Rect((lid.w - width) / 2, lid.bottom, width, .18))
}

private fun browserMask(r: Rect, radius: Double): Path {
    val reach = min(radius, min(r.w, r.h) / 2)
    val c = reach * (1 - Paths.KAPPA)
    return PathBuilder().moveTo(r.x, r.y).lineTo(r.right, r.y).lineTo(r.right, r.bottom - reach)
        .cubicTo(r.right, r.bottom - c, r.right - c, r.bottom, r.right - reach, r.bottom)
        .lineTo(r.x + reach, r.bottom).cubicTo(r.x + c, r.bottom, r.x, r.bottom - c, r.x, r.bottom - reach)
        .lineTo(r.x, r.y).close().build()
}

// Extras: cameras / toolbar controls / laptop notch. Crease and optional home/glare are painted separately.
private fun FrameSpec.decorations(screen: Rect, lid: Rect): List<Path> = when (kind) {
    FrameKind.TABLET_UNIFORM, FrameKind.TABLET_ANDROID -> if (Extra.CAMERA !in extras) emptyList() else listOf(Paths.ellipse(Rect(lid.w / 2 - .005, bezel.top / 2 - .005, .010, .010)))
    FrameKind.LAPTOP -> buildList {
        if (Extra.CAMERA in extras) add(Paths.ellipse(Rect(lid.w / 2 - .003, bezel.top / 2 - .003, .006, .006)))
        if (Extra.LAPTOP_BASE in extras) add(roundedRect(Rect(lid.w / 2 - .07, lid.bottom, .14, .008), .004, 0.0))
    }
    FrameKind.BROWSER -> if (Extra.BROWSER_BAR !in extras) emptyList() else listOf(.030, .050, .070).map { x -> Paths.ellipse(Rect(x - .006, .019, .012, .012)) } +
        roundedRect(Rect(.12, .012, .76, .026), .013, 0.0)
    FrameKind.FOLDABLE_OPEN -> if (Extra.CREASE !in extras) emptyList() else listOf(Paths.rect(Rect(screen.x + .498, screen.y, .004, screen.h)))
    else -> emptyList()
}

private fun Cutout.path(screen: Rect): Path = when (this) {
    is Cutout.Island -> roundedRect(Rect(screen.x + (1 - w) / 2, screen.y + top, w, h), h / 2, 0.0)
    is Cutout.Hole -> Paths.ellipse(Rect(screen.x + cx - d / 2, screen.y + top, d, d))
    is Cutout.Notch -> {
        val left = screen.x + (1 - w) / 2
        val right = left + w
        val top = screen.y
        val bottom = top + h
        val k = Paths.KAPPA
        PathBuilder().moveTo(left - shoulder, top).lineTo(right + shoulder, top)
            .cubicTo(right + shoulder * (1 - k), top, right, top + shoulder * (1 - k), right, top + shoulder)
            .lineTo(right, bottom - bottomRadius)
            .cubicTo(right, bottom - bottomRadius * (1 - k), right - bottomRadius * (1 - k), bottom, right - bottomRadius, bottom)
            .lineTo(left + bottomRadius, bottom)
            .cubicTo(left + bottomRadius * (1 - k), bottom, left, bottom - bottomRadius * (1 - k), left, bottom - bottomRadius)
            .lineTo(left, top + shoulder)
            .cubicTo(left, top + shoulder * (1 - k), left - shoulder * (1 - k), top, left - shoulder, top).close().build()
    }
}

private fun buttonRect(body: Rect, b: Button): Rect = when (b.side) {
    Side.LEFT -> Rect(-b.protrusion, body.h * b.from, b.protrusion, body.h * (b.to - b.from))
    Side.RIGHT -> Rect(body.right, body.h * b.from, b.protrusion, body.h * (b.to - b.from))
    Side.TOP -> Rect(body.w * b.from, -b.protrusion, body.w * (b.to - b.from), b.protrusion)
    Side.BOTTOM -> Rect(body.w * b.from, body.bottom, body.w * (b.to - b.from), b.protrusion)
}

internal fun Rect.mapped(m: Affine): Rect {
    val p = listOf(Point(x, y), Point(right, y), Point(right, bottom), Point(x, bottom)).map { m.apply(it) }
    val x0 = p.minOf { it.x }
    val y0 = p.minOf { it.y }
    return Rect(x0, y0, p.maxOf { it.x } - x0, p.maxOf { it.y } - y0)
}

internal fun Path.mapped(m: Affine): Path = Path(ops.map { op ->
    when (op) {
        is PathOp.Move -> m.apply(Point(op.x, op.y)).let { PathOp.Move(it.x, it.y) }
        is PathOp.Line -> m.apply(Point(op.x, op.y)).let { PathOp.Line(it.x, it.y) }
        is PathOp.Cubic -> {
            val a = m.apply(Point(op.c1x, op.c1y))
            val b = m.apply(Point(op.c2x, op.c2y))
            val c = m.apply(Point(op.x, op.y))
            PathOp.Cubic(a.x, a.y, b.x, b.y, c.x, c.y)
        }
        PathOp.Close -> PathOp.Close
    }
})

private fun union(rects: List<Rect>): Rect {
    val x = rects.minOf { it.x }
    val y = rects.minOf { it.y }
    return Rect(x, y, rects.maxOf { it.right } - x, rects.maxOf { it.bottom } - y)
}
